import { createCacheKey, ENDPOINTS, useCreatePayload } from 'hammock';
import { useMemo } from 'react';
import { useQueryClient } from 'react-query';

import { useCustomFonts, WebFontResource } from '@candu-ai/styleguide/custom-fonts';
import { Variants } from '@candu-ai/styleguide/types';
import { useGoogleFonts } from '@candu-ai/styleguide/ui';

const sortFonts = (a: WebFontResource, b: WebFontResource) => (a.family > b.family ? 1 : -1);

export const useStyleguideFonts = () => {
  const queryClient = useQueryClient();
  const { fonts: allCustomFonts } = useCustomFonts();
  const allGoogleFonts = useGoogleFonts();
  const { getAll } = useCreatePayload(ENDPOINTS.styleguideVariants);

  const versionsQueries = getAll({});

  const { variants: styleguideVariantsData } = (queryClient.getQueriesData(
    createCacheKey(versionsQueries),
  )[0] as Variants)[1];

  const styleguideFonts = useMemo(() => {
    const usedFonts = new Set();

    Object.values(styleguideVariantsData).forEach((variant) => {
      Object.values(variant).forEach(({ selectors }) => {
        if (selectors) {
          Object.values(selectors).forEach(({ rules }) => {
            const fontFamily = rules['--font-family'];
            if (fontFamily && !fontFamily.includes('var(')) {
              usedFonts.add(fontFamily);
            }
          });
        }
      });
    });

    return usedFonts;
  }, [styleguideVariantsData]);

  const availableFonts = useMemo(
    () =>
      [...(allCustomFonts || []), ...allGoogleFonts].filter(({ family }) =>
        styleguideFonts.has(family),
      ),
    [allCustomFonts, allGoogleFonts, styleguideFonts],
  );

  return availableFonts.sort(sortFonts);
};
